import { useTranslation } from 'react-i18next'
import { type ColumnDef } from '@tanstack/react-table'

import { DataTable, optionalColumn, selectionColumn } from '@/components/ui/data-table'
import type { ListSort } from '@/services/core/paging'
import { DateCell, MutedCell, NameCell, StatusChip } from '@/components/TableCells'
import { localeLabel } from '@/data/userLocales'
import type { ReportTranslation } from '@/services/reports/reportTranslationsApi'

/** The API field each sortable column orders by when the server sorts the list. */
const SORT_FIELDS: Record<string, string> = {
  name: 'name',
  locale: 'locale',
  defaultTranslation: 'defaultTranslation',
  objectState: 'objectState',
  updateTime: 'updateTime',
}

interface ReportTranslationTableProps {
  translations: ReportTranslation[]
  loading?: boolean
  emptyMessage?: string
  selection?: ReportTranslation[]
  onSelectionChange?: (translations: ReportTranslation[]) => void
  /** Sorts on the server — pass both for a list the server pages. */
  sort?: ListSort | null
  onSortChange?: (sort: ListSort | null) => void
}

export function ReportTranslationTable({
  translations,
  loading,
  emptyMessage,
  selection,
  onSelectionChange,
  sort,
  onSortChange,
}: ReportTranslationTableProps) {
  const { t, i18n } = useTranslation()
  const selectable = !!onSelectionChange

  const columns: ColumnDef<ReportTranslation, unknown>[] = [
    ...(selectable ? [selectionColumn<ReportTranslation>()] : []),
    {
      accessorKey: 'name',
      header: t('common.name'),
      meta: { minWidth: 220 },
      cell: ({ row }) => <NameCell name={row.original.name} id={row.original.id} />,
    },
    {
      accessorKey: 'locale',
      header: t('reportTranslations.locale'),
      size: 200,
      cell: ({ row }) => <MutedCell value={localeLabel(row.original.locale, i18n.language)} />,
    },
    {
      accessorKey: 'defaultTranslation',
      header: t('reportTranslations.baseLanguage'),
      size: 150,
      cell: ({ row }) => (
        <MutedCell value={t(`reportTranslations.languages.${row.original.defaultTranslation}`)} />
      ),
    },
    {
      id: 'overrides',
      accessorFn: (row) => Object.keys(row.translations).length,
      header: t('reportTranslations.overrides'),
      size: 120,
      cell: ({ row }) => <MutedCell value={Object.keys(row.original.translations).length} />,
    },
    {
      accessorKey: 'objectState',
      header: t('common.status'),
      size: 110,
      cell: ({ row }) => <StatusChip state={row.original.objectState} />,
    },
    optionalColumn<ReportTranslation>({
      accessorKey: 'description',
      header: t('common.description'),
      enableSorting: false,
      meta: { minWidth: 220 },
      cell: ({ row }) => <MutedCell value={row.original.description} />,
    }),
    optionalColumn<ReportTranslation>({
      accessorKey: 'updateTime',
      header: t('common.updated'),
      size: 120,
      cell: ({ row }) => <DateCell value={row.original.updateTime} />,
    }),
  ]

  return (
    <DataTable
      columns={columns}
      data={translations}
      loading={loading}
      emptyMessage={emptyMessage ?? t('reportTranslations.noTranslationsFound')}
      columnsKey="reportTranslations"
      rowHref={(row) => `/reports/translations/${row.id}`}
      {...(selectable ? { selection, onSelectionChange } : {})}
      serverSort={
        onSortChange && { sort: sort ?? null, onChange: onSortChange, fields: SORT_FIELDS }
      }
    />
  )
}
